import { useEffect, useRef } from 'react';
import type { SlashCommandDefinition } from '../chat-commands';
import { slashMenuItemId } from './slash-menu-utils';

interface SlashCommandMenuProps {
  commands: SlashCommandDefinition[];
  selectedIndex: number;
  onSelect: (command: SlashCommandDefinition) => void;
  onClose: () => void;
  /** Stage label shown above the list (e.g., "Select target") */
  stageLabel?: string | null;
  /** When true, show "No matching commands" instead of hiding (for filtered empty state) */
  hasActiveFilter?: boolean;
}

/** Render text with fuzzy match positions highlighted */
function HighlightedText({
  text,
  positions,
  className,
}: {
  text: string;
  positions?: number[];
  className?: string;
}) {
  if (!positions?.length) {
    return <span className={className}>{text}</span>;
  }

  const posSet = new Set(positions);
  const parts: { text: string; highlighted: boolean }[] = [];
  let current = '';
  let currentHighlighted = false;

  for (let i = 0; i < text.length; i++) {
    const isHighlighted = posSet.has(i);
    if (i === 0) {
      currentHighlighted = isHighlighted;
      current = text[i];
    } else if (isHighlighted === currentHighlighted) {
      current += text[i];
    } else {
      parts.push({ text: current, highlighted: currentHighlighted });
      current = text[i];
      currentHighlighted = isHighlighted;
    }
  }
  if (current) parts.push({ text: current, highlighted: currentHighlighted });

  return (
    <span className={className}>
      {parts.map((part, i) =>
        part.highlighted ? (
          <span key={i} className="text-primary font-bold">
            {part.text}
          </span>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </span>
  );
}

export function SlashCommandMenu({
  commands,
  selectedIndex,
  onSelect,
  onClose,
  stageLabel,
  hasActiveFilter,
}: SlashCommandMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Trim stale refs when list shrinks (prevents scrollIntoView on detached nodes)
  itemRefs.current = itemRefs.current.slice(0, commands.length);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= commands.length) return;
    const selectedItem = itemRefs.current[selectedIndex];
    if (selectedItem) {
      selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex, commands.length]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Empty state: show message when filter yields no results
  if (commands.length === 0) {
    if (!hasActiveFilter) return null;
    return (
      <div
        ref={menuRef}
        className="absolute bottom-full left-0 right-0 mb-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden z-50"
        role="listbox"
        aria-label="Slash commands"
      >
        {stageLabel && (
          <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground border-b border-border bg-muted/50">
            {stageLabel}
          </div>
        )}
        <div className="px-3 py-4 text-sm text-muted-foreground text-center">
          No matching commands
        </div>
      </div>
    );
  }

  return (
    <div
      ref={menuRef}
      className="absolute bottom-full left-0 right-0 mb-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden z-50"
      role="listbox"
      aria-label="Slash commands"
    >
      {/* Header: stage label or result count */}
      {stageLabel ? (
        <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground border-b border-border bg-muted/50">
          {stageLabel}
        </div>
      ) : (
        commands.length > 5 && (
          <div className="px-3 py-1 text-[11px] text-muted-foreground/60 border-b border-border bg-muted/30">
            {commands.length} commands
          </div>
        )
      )}
      <div className="max-h-64 overflow-y-auto py-1">
        {commands.map((cmd, index) => (
          <button
            key={cmd.sourceId ? `${cmd.name}-${cmd.sourceId}` : `${cmd.name}-${index}`}
            id={slashMenuItemId(index)}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            role="option"
            aria-selected={index === selectedIndex}
            className={`w-full px-3 py-1.5 flex items-center gap-3 text-left transition-colors ${
              index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
            }`}
            onClick={() => onSelect(cmd)}
          >
            {cmd.isMenuOption ? (
              // Sub-menu option: no / prefix, just name + description
              <>
                {/* Multi-select checkbox column. Rendered for every row in a
                 *  multi-select stage (i.e. when ANY cmd in the list has
                 *  `isChecked` defined), so checked/unchecked rows align.
                 *  The Confirm pseudo-item suppresses its own checkbox via
                 *  `isMultiSelectConfirm`. */}
                {cmd.isMultiSelectConfirm ? null : cmd.isChecked === true ? (
                  <span
                    className="shrink-0 w-4 h-4 rounded-sm border border-primary bg-primary/20 flex items-center justify-center text-primary text-xs leading-none"
                    aria-hidden="true"
                  >
                    ✓
                  </span>
                ) : cmd.isChecked === false ? (
                  <span
                    className="shrink-0 w-4 h-4 rounded-sm border border-muted-foreground/40"
                    aria-hidden="true"
                  />
                ) : null}
                {cmd.avatarUrl || cmd.emoji ? (
                  // Character pickers: portrait/avatar stacks over the
                  // emoji in a fixed 24px box. The emoji sits absolutely
                  // positioned behind the image so that:
                  //   - When the image loads, it covers the emoji.
                  //   - When the image errors (display:none below), the
                  //     emoji shows through — no dead space.
                  // Mirrors mobile `CharAvatar`'s stacking pattern.
                  <span className="relative shrink-0 inline-flex h-6 w-6 items-center justify-center">
                    {cmd.emoji && (
                      <span
                        className="absolute inset-0 flex items-center justify-center text-base"
                        aria-hidden="true"
                      >
                        {cmd.emoji}
                      </span>
                    )}
                    {cmd.avatarUrl && (
                      <img
                        src={cmd.avatarUrl}
                        alt=""
                        aria-hidden="true"
                        className="relative h-6 w-6 rounded-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </span>
                ) : null}
                <HighlightedText
                  text={cmd.name}
                  positions={cmd.matchPositions}
                  className="text-sm font-medium text-foreground shrink-0 truncate max-w-[50%]"
                />
                {cmd.description && (
                  <span className="text-sm text-muted-foreground truncate">{cmd.description}</span>
                )}
                {cmd.shortcutHint && (
                  <span className="ml-auto text-xs text-muted-foreground/50 font-mono shrink-0">
                    {cmd.shortcutHint}
                  </span>
                )}
              </>
            ) : (
              // Slash command: /name + description + optional shortcut
              <>
                <span className="font-mono text-sm font-medium text-primary shrink-0">
                  /
                  <HighlightedText text={cmd.name} positions={cmd.matchPositions} />
                  {cmd.hasArg && (
                    <span className="text-muted-foreground ml-1">
                      {'<'}
                      {cmd.argPlaceholder}
                      {'>'}
                    </span>
                  )}
                </span>
                <span className="text-sm text-muted-foreground truncate">{cmd.description}</span>
                {cmd.shortcutHint && (
                  <span className="ml-auto text-xs text-muted-foreground/50 font-mono shrink-0">
                    {cmd.shortcutHint}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
